// disabling for file because it becomes unreadable when required in utility functions
/* eslint-disable arrow-body-style */

import { NavigationEnd, Router } from '@angular/router';

import { Observable, OperatorFunction, pipe } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import {
  NotificationService,
  NotificationsState,
} from '@app/notifications/notification.service';
import { AuthUser } from '@degreed/lxp/services/authorization';
import { LayoutAspect, LayoutConfiguration } from '@degreed/lxp/ui';
import { produce } from 'immer';

import { BadgeRole } from '@degreed/lxp/services';
import { updateOrgId } from './layout-configuration.utils';

/**
 *  Select the Admin layout configuration with updated Org Ids
 */
export const selectAdminAspect = (authUser: AuthUser, enableLog = false) => {
  return pipe(
    map((layout: LayoutConfiguration) => layout.admin),
    updateAdminOrgIds(authUser),
    tap((aspect) => enableLog && console.log(aspect))
  );
};

/**
 * Only allow Learner layout; filter out Admin layout
 */
export const selectLearnerAspect = (
  authUser: AuthUser,
  router: Router,
  enableLog = false
): OperatorFunction<LayoutConfiguration, LayoutAspect> => {
  return pipe(
    map((layout: LayoutConfiguration) => layout.learner),
    updateLearnerOrgIds(authUser),
    switchMap(buildLearnerRouteWatch(router)),
    distinctUntilChanged(),
    tap((aspect) => {
      return enableLog && console.log(aspect);
    })
  );
};

/**
 *  Watches for notification changes so we can update the indicator that there are new notifications or notifications have been viewed.
 */

export const watchNotificationStatusChanges = (
  notificationService: NotificationService,
  enableLog = false
): OperatorFunction<LayoutConfiguration, LayoutAspect> => {
  return pipe(
    // The notification watch logic is encapsulated here.
    map((layout: LayoutConfiguration) => layout.learner),
    switchMap(buildLearnerNotificationWatch(notificationService)),
    distinctUntilChanged(),
    tap((notificationAspect) => enableLog && console.log(notificationAspect))
  );
};

const updateAdminOrgIds = (authUser: AuthUser) =>
  pipe(map((admin) => updateOrgId<LayoutAspect>(admin, authUser, true)));

const updateLearnerOrgIds = (authUser: AuthUser) =>
  pipe(map((learner) => updateOrgId<LayoutAspect>(learner, authUser, true)));

/**
 * When route changes (observed by router.events),
 * update the layout configuration to hide/show options
 */
const buildLearnerRouteWatch = (
  router: Router
): ((learner: LayoutAspect) => Observable<LayoutAspect>) => {
  const navigateEnd$ = router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    startWith({ url: window.location.pathname })
  );

  return (learner: LayoutAspect): Observable<LayoutAspect> => {
    return navigateEnd$.pipe(
      map(({ url }: NavigationEnd) => {
        const isSearchVisible = learner.features?.search?.visible;
        const showGlobalSearch = !url.includes('/learning');
        const shouldUpdate = isSearchVisible !== showGlobalSearch;

        return produce(learner, (draft) => {
          /**
           * Should we show the Search features in the header area? Since the Search content pages already
           * have their own search bar, we hide the global search bar when viewing the `/learning` pages.
           */
          if (shouldUpdate && draft.features?.search) {
            draft.features.search.visible = showGlobalSearch;
          }
        });
      })
    );
  };
};

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
const buildLearnerNotificationWatch = (
  notificationService: NotificationService
): ((learner: LayoutAspect) => Observable<LayoutAspect>) => {
  return (learner: LayoutAspect): Observable<LayoutAspect> =>
    notificationService.notificationsState.pipe(
      map((state: NotificationsState) =>
        produce(learner, (draft) => {
          const notificationsNode = draft.navigation.top.find(
            (node) => node.badge === BadgeRole.notification
          );
          if (notificationsNode) {
            notificationsNode.badgeVisible = state.newNotificationCount > 0;
          }
        })
      )
    );
};
